/**
 * 预算调整对话框（行内「改预算」与批量改预算共用）。
 *
 * 前端预检（与后端同一套护栏规则，见 lib/budgetGuard.ts）：
 *   - 解析输入为整数分（最多两位小数，不四舍五入）；
 *   - 逐目标跑 `checkBudgetChange`：单次最多调高 3 倍（改小不限）、1.00–100000.00、1.00 的整数倍；
 *   - 任一目标有问题就**禁用提交**，并把每一条原因摆在用户面前。
 *
 * 预检通过 ≠ 后端一定放行：点提交后仍会先走 dry-run，由 ActionPlanDialog 展示
 * 后端逐条判定结果，再二次确认。
 */

import { useEffect, useMemo, useState, type ReactElement } from "react";
import { Modal } from "./Modal.js";
import { BudgetHistoryList } from "./BudgetHistoryList.js";
import { checkBudgetChange, parseAmountToMinorUnits, type BudgetGuardIssue } from "../lib/budgetGuard.js";
import { DASH, formatCentsOrDash } from "../lib/format.js";
import type { AdEntityRow } from "../lib/apiTypes.js";

export interface BudgetDialogProps {
  readonly open: boolean;
  readonly targets: readonly AdEntityRow[];
  readonly initialValue: string;
  readonly busy: boolean;
  /**
   * `true` = 该目标 `budgetScope === "both"`（两层都有预算）→ **必须显式选层**才能提交
   * （P2 任务 4）。缺省 `false`：不显示这个选项（避免打扰）。
   */
  readonly budgetLevelRequired?: boolean;
  /** 必选层时展示的警示文案（由页面用 `explicitBudgetLevelWarning` 生成）。 */
  readonly budgetLevelWarning?: string;
  readonly onClose: () => void;
  readonly onSubmit: (minorUnits: number, budgetLevel: "campaign" | "adset" | null) => void;
}

interface TargetCheck {
  readonly row: AdEntityRow;
  readonly issues: readonly BudgetGuardIssue[];
}

export function BudgetDialog({
  open,
  targets,
  initialValue,
  busy,
  budgetLevelRequired = false,
  budgetLevelWarning,
  onClose,
  onSubmit,
}: BudgetDialogProps): ReactElement {
  const [value, setValue] = useState(initialValue);
  /**
   * 显式选层（P2 任务 4）：**默认不选**（`""`）。`both` 时未选中 → 提交按钮禁用。
   * 非 `both` 时这个 state 恒为 `""`（不渲染选择器），提交时传 `null`。
   */
  const [budgetLevel, setBudgetLevel] = useState<"" | "campaign" | "adset">("");

  // 每次打开都用最新的初值（行内改预算 = 当前值；批量 = 批量栏输入或空）
  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setBudgetLevel("");
    }
  }, [open, initialValue]);

  const parsed = parseAmountToMinorUnits(value);
  const checks = useMemo<TargetCheck[]>(() => {
    if (parsed === null) return [];
    return targets.map((row) => ({
      row,
      issues: checkBudgetChange({ beforeMinorUnits: row.dailyBudgetCents, afterMinorUnits: parsed }),
    }));
  }, [parsed, targets]);

  const allIssues = useMemo(() => {
    const seen = new Map<string, BudgetGuardIssue>();
    for (const check of checks) {
      for (const issue of check.issues) seen.set(issue.code, issue);
    }
    return [...seen.values()];
  }, [checks]);

  const formatIssue = parsed === null && value.trim() !== "" ? "金额格式非法：最多两位小数、不接受负数/科学计数法" : null;
  const levelMissing = budgetLevelRequired && budgetLevel === "";
  const blocked = parsed === null || allIssues.length > 0 || levelMissing;
  const currency = targets[0]?.currency ?? "USD";
  /** 单实体弹窗才显示预算历史（多实体时"该实体"没有唯一答案）。 */
  const singleTarget: AdEntityRow | null = targets.length === 1 ? (targets[0] ?? null) : null;

  return (
    <Modal
      open={open}
      title="调整日预算"
      description={`将把 ${targets.length} 个目标的 daily_budget 设为同一个新值；先本地预检，再 dry-run，再二次确认。`}
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-control border border-line px-3 text-table text-ink transition-colors dur-fast hover:bg-hover disabled:opacity-40"
            style={{ height: "var(--control-height)" }}
          >
            取消
          </button>
          <button
            type="button"
            data-testid="budget-submit"
            onClick={() => {
              if (parsed !== null && !blocked) onSubmit(parsed, budgetLevel === "" ? null : budgetLevel);
            }}
            disabled={busy || blocked}
            className="rounded-control bg-accent px-3 text-table font-medium text-accent-ink transition-colors dur-fast hover:bg-accent-hover disabled:opacity-40"
            style={{ height: "var(--control-height)" }}
          >
            {busy ? "预演中…" : "下一步：dry-run 预演"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block text-table font-medium text-ink" htmlFor="budget-new-value">
          新日预算（{currency}）
        </label>
        <input
          id="budget-new-value"
          value={value}
          inputMode="decimal"
          autoComplete="off"
          onChange={(event) => setValue(event.target.value)}
          className="w-full rounded-control border border-line bg-surface px-2 text-table text-ink outline-none focus-visible:border-accent"
          style={{ height: "var(--control-height)" }}
        />
        <p className="text-2xs text-ink-3">
          护栏：单次最多调高到 3 倍（改小不限）· 额度 1.00–100000.00 · 必须是 1.00 的整数倍；金额按整数分运算。
        </p>

        {/*
          P2 任务 4：`both`（两层都有预算）时**必须显式选层**。
          非 `both` 时不渲染这个选择器（避免打扰）；`both` 时默认空 → 不能提交。
        */}
        {budgetLevelRequired ? (
          <div className="space-y-2 rounded-control border border-warning/40 bg-warning-subtle px-3 py-2">
            <p className="text-meta text-warning-text">{budgetLevelWarning ?? "该系列两层都有预算：请显式选择要改哪一层。"}</p>
            <label className="block text-table font-medium text-ink" htmlFor="budget-level">
              预算改在哪一层（必选）
            </label>
            <select
              id="budget-level"
              data-testid="budget-level-select"
              value={budgetLevel}
              onChange={(event) => setBudgetLevel(event.target.value as "" | "campaign" | "adset")}
              className="w-full rounded-control border border-line bg-surface px-2 text-table text-ink outline-none focus-visible:border-accent"
              style={{ height: "var(--control-height)" }}
            >
              <option value="">请选择…</option>
              <option value="campaign">系列层（campaign）</option>
              <option value="adset">广告组层（adset）</option>
            </select>
            <p className="text-2xs text-ink-3">
              服务端会校验：选择必须与本次目标的实际层级一致，否则**拒绝执行**（不会替你改另一层）。
            </p>
          </div>
        ) : null}

        {formatIssue === null ? null : (
          <p className="rounded-control border border-danger/40 bg-danger-subtle px-3 py-2 text-meta text-danger-text">
            {formatIssue}
          </p>
        )}

        {allIssues.length === 0 ? null : (
          <ul className="list-disc space-y-1 rounded-control border border-warning/40 bg-warning-subtle px-5 py-2 text-meta text-warning-text">
            {allIssues.map((issue) => (
              <li key={issue.code}>
                <span className="font-mono text-2xs">{issue.code}</span> · {issue.message}
              </li>
            ))}
          </ul>
        )}

        <table className="w-full border-collapse text-table">
          <thead>
            <tr className="border-b border-line-subtle text-left text-meta text-ink-3">
              <th className="py-1 font-medium">目标</th>
              <th className="py-1 text-right font-medium">原值</th>
              <th className="py-1 text-right font-medium">新值</th>
              <th className="py-1 text-right font-medium">本地预检</th>
            </tr>
          </thead>
          <tbody className="tnum">
            {targets.map((row) => {
              const check = checks.find((candidate) => candidate.row.id === row.id);
              const issues = check?.issues ?? [];
              return (
                <tr key={row.id} className="border-b border-line-subtle">
                  <td className="max-w-[220px] truncate py-1 text-ink" title={row.name}>
                    {row.name}
                  </td>
                  <td className="py-1 text-right text-ink-2">{formatCentsOrDash(row.dailyBudgetCents, row.currency)}</td>
                  <td className="py-1 text-right text-ink">
                    {parsed === null ? DASH : formatCentsOrDash(parsed, row.currency)}
                  </td>
                  <td className="py-1 text-right text-2xs">
                    {issues.length === 0 ? (
                      <span className="text-success-text">通过</span>
                    ) : (
                      <span className="text-warning-text">{issues.map((issue) => issue.code).join(", ")}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/*
          第十二批 · 用户第 8 条：显示该实体的**最近 10 次预算调整**（调整前 → 调整后 + 时间，
          每天只留最后一次）。只在**单实体**弹窗里显示——多实体批量时"该实体"没有唯一答案，
          后端接口也是按 (accountId, level, entityId) 查的（ABO 批量弹窗走另一条汇总查询）。
        */}
        {singleTarget === null ? null : (
          <BudgetHistoryList
            accountId={singleTarget.accountId}
            level={singleTarget.level}
            entityId={singleTarget.id}
            currency={singleTarget.currency}
            limit={10}
          />
        )}
      </div>
    </Modal>
  );
}
